import Papa from 'papaparse';
import { foldHeading } from '~/components/general/createCsvValidator';
import { preparePapaParseWorker } from '~/utils/papaparse';

const ERRORS_MESSAGES = {
    encoding: (rowIndex) => `Invalid character at row ${rowIndex}. Make sure your CSV has UTF-8 encoding.`,
    headingEmpty: () => 'Found an empty heading.',
    headingLength: (heading) => `Heading "${heading}" is too long.`,
    headingUnique: (heading) => `Heading "${heading}" must be unique.`,
    headingCouponCodeRequired: () => `Heading "coupon_code" is required.`,
    invalidColumns: (rowIndex) => `Wrong number of values at row ${rowIndex}.`,
    failedToProcess: (rowIndex, message) =>
        `It was not possible to process row ${rowIndex}.` + (message ? ` ${message}` : ''),
};

let errorMessage = '';

const handleError = (message, parser) => {
    errorMessage = message;
    parser.abort();
};

/**
 * @param {File} file
 */
export function validatePromotionCouponsCsv(file) {
    errorMessage = '';

    return new Promise((resolve, reject) => {
        if (!file?.type || file.type !== 'text/csv') {
            reject({
                message: 'The file is not a CSV. Please use CSV file in order to proceed with import.',
            });
            return;
        }

        if (file.size > 100 * 1024 ** 2) {
            reject({
                message: 'Max file size is 100MiB.',
            });
            return;
        }

        // Parse the CSV file and validate data.
        let rowsCount = 0;

        let csvHeadings = [];

        preparePapaParseWorker();

        Papa.parse(file, {
            delimiter: ',',
            worker: true,
            skipEmptyLines: true,
            step: (results, parser) => {
                rowsCount++;
                // Handle parser errors.
                if (results.errors.length) {
                    handleError(
                        ERRORS_MESSAGES.failedToProcess(results.errors[0].row, results.errors[0].message),
                        parser
                    );
                    return;
                }

                const data = results.data;

                // Check UTF-8 encoding.
                for (const value of data) {
                    // Check whether value has �.
                    // If it is so, it either means the file does not use UTF-8 or � is really present in value.
                    if (value && value.indexOf('�') >= 0) {
                        handleError(ERRORS_MESSAGES.encoding(rowsCount), parser);
                        return;
                    }
                }

                if (rowsCount === 1) {
                    // Validate headings.
                    const headings = data.map(foldHeading);

                    if (!headings.includes('coupon_code')) {
                        handleError(ERRORS_MESSAGES.headingCouponCodeRequired(), parser);
                        return;
                    }

                    for (const [index, heading] of headings.entries()) {
                        if (!heading) {
                            handleError(ERRORS_MESSAGES.headingEmpty(), parser);
                            return;
                        }

                        if (heading.length > 80) {
                            handleError(ERRORS_MESSAGES.headingLength(data[index]), parser);
                            return;
                        }

                        if (headings.indexOf(heading) !== index) {
                            handleError(ERRORS_MESSAGES.headingUnique(data[index]), parser);
                            return;
                        }
                    }

                    csvHeadings = headings;

                    return;
                }

                if (data.length !== csvHeadings.length) {
                    handleError(ERRORS_MESSAGES.invalidColumns(rowsCount), parser);
                    return;
                }
            },
            complete: () => {
                if (rowsCount <= 0 && !errorMessage) {
                    reject({
                        message:
                            'The CSV file does not contain any data. Please go back and make corrections to the file.',
                    });
                    return;
                }

                resolve({
                    validCount: rowsCount > 1 ? rowsCount - 1 : 0,
                    message: errorMessage,
                });
            },
            error: () => {
                reject({
                    message: 'It was not possible to process the CSV file. Please contact support.',
                });
            },
        });
    });
}
